import { useEffect, useRef } from "react";
import { Head, Link, useForm } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import { manage_organization_faq_path, manage_organizations_path } from "@/routes";

function formValues(faq) {
  return {
    slug: faq.slug || "",
    title: faq.title || "",
    introduction: faq.introduction || "",
    entries_attributes: (faq.entries || []).map((entry, index) => ({
      ...entry,
      key: entry.id ? `saved-${entry.id}` : `initial-${index}`
    }))
  };
}

function OrganizationFaqEdit({ organization, faq, faq_url: faqUrl, faq_base_url: faqBaseUrl, errors = [] }) {
  const nextEntryId = useRef(0);
  const { data, setData, patch, transform, processing } = useForm(formValues(faq));
  const entries = data.entries_attributes.filter((entry) => !entry._destroy);

  useEffect(() => {
    setData(formValues(faq));
  }, [faq, setData]);

  const updateEntry = (key, attributes) => {
    setData("entries_attributes", data.entries_attributes.map((entry) => (
      entry.key === key ? { ...entry, ...attributes } : entry
    )));
  };

  const addEntry = () => {
    nextEntryId.current += 1;
    setData("entries_attributes", [
      ...data.entries_attributes,
      { key: `new-${nextEntryId.current}`, question: "", answer: "" }
    ]);
  };

  const removeEntry = (entry) => {
    if (entry.id) {
      updateEntry(entry.key, { _destroy: true });
    } else {
      setData("entries_attributes", data.entries_attributes.filter((item) => item.key !== entry.key));
    }
  };

  const submit = (event) => {
    event.preventDefault();
    transform((values) => ({
      organization_faq: {
        ...values,
        entries_attributes: values.entries_attributes.map(({ key, ...entry }) => entry)
      }
    }));
    patch(manage_organization_faq_path(organization.id), { preserveScroll: true });
  };

  return (
    <>
      <Head title={`FAQ · ${organization.name}`} />

      <section className="workspace-content">
        <form className="vstack gap-4" onSubmit={submit}>
          {errors.length > 0 ? (
            <div className="alert alert-danger mb-0" role="alert">{errors.join(", ")}</div>
          ) : null}

          <div className="card">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div>
                  <div className="workspace-section__eyebrow">{organization.name}</div>
                  <h2 className="workspace-section__title">Offentlig FAQ</h2>
                  <p className="workspace-admin-copy">
                    Saml organisationens spørgsmål og svar på en side, der kan læses uden login.
                  </p>
                </div>
                <span className="workspace-chip">{faqUrl ? "Offentlig" : "Ikke aktiveret"}</span>
              </div>

              <div className="vstack gap-4 mt-4">
                <div>
                  <label htmlFor="faq-slug" className="form-label fw-semibold">Unik FAQ-URL</label>
                  <div className="input-group">
                    <span className="input-group-text">{faqBaseUrl}</span>
                    <input
                      id="faq-slug"
                      className="form-control"
                      value={data.slug}
                      onChange={(event) => setData("slug", event.target.value)}
                      placeholder="organisation-faq"
                      maxLength={100}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      aria-describedby="faq-slug-help"
                    />
                  </div>
                  <div id="faq-slug-help" className="form-text">
                    Brug små bogstaver, tal og bindestreger. Gem med en URL for at aktivere FAQ’en.
                    Fjern URL’en og gem for at deaktivere den og beholde indholdet.
                  </div>
                  {faqUrl ? (
                    <a href={faqUrl} target="_blank" rel="noreferrer" className="d-inline-block mt-2">
                      Åbn offentlig FAQ <i className="bi bi-box-arrow-up-right ms-1" aria-hidden="true" />
                    </a>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="faq-title" className="form-label fw-semibold">Overskrift</label>
                  <input
                    id="faq-title"
                    className="form-control"
                    value={data.title}
                    onChange={(event) => setData("title", event.target.value)}
                    placeholder={`${organization.name}: Vejledning og FAQ`}
                  />
                </div>

                <div>
                  <label htmlFor="faq-introduction" className="form-label fw-semibold">Introduktion</label>
                  <textarea
                    id="faq-introduction"
                    className="form-control"
                    rows={3}
                    value={data.introduction}
                    onChange={(event) => setData("introduction", event.target.value)}
                    placeholder="Beskriv, hvem FAQ’en er til, og hvad læseren kan få hjælp til."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <h2 className="workspace-section__title">Spørgsmål og svar</h2>
                  <p className="workspace-admin-copy mb-0">
                    Svar kan formateres med Markdown: fed tekst, punktopstillinger, links og tabeller.
                  </p>
                </div>
                <button type="button" className="btn btn-outline-secondary" onClick={addEntry} disabled={processing}>
                  <i className="bi bi-plus-lg me-2" aria-hidden="true" />Tilføj spørgsmål
                </button>
              </div>

              {entries.length === 0 ? (
                <div className="workspace-admin-list__empty mt-4">
                  Der er ingen spørgsmål endnu. Tilføj det første spørgsmål for at komme i gang.
                </div>
              ) : (
                <div className="vstack gap-4 mt-4">
                  {entries.map((entry, index) => (
                    <fieldset key={entry.key} className="border rounded-3 p-3 p-md-4">
                      <legend className="float-none w-auto px-2 fs-6 fw-semibold">Spørgsmål {index + 1}</legend>
                      <div className="vstack gap-3">
                        <div>
                          <label htmlFor={`question-${entry.key}`} className="form-label fw-semibold">Spørgsmål</label>
                          <input
                            id={`question-${entry.key}`}
                            className="form-control"
                            value={entry.question}
                            onChange={(event) => updateEntry(entry.key, { question: event.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor={`answer-${entry.key}`} className="form-label fw-semibold">Svar</label>
                          <textarea
                            id={`answer-${entry.key}`}
                            className="form-control"
                            rows={6}
                            value={entry.answer}
                            onChange={(event) => updateEntry(entry.key, { answer: event.target.value })}
                            required
                          />
                        </div>
                        <div className="d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => removeEntry(entry)}
                            disabled={processing}
                            aria-label={`Fjern spørgsmål ${index + 1}`}
                          >
                            <i className="bi bi-trash me-2" aria-hidden="true" />Fjern
                          </button>
                        </div>
                      </div>
                    </fieldset>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="d-flex flex-wrap justify-content-end gap-2">
            <Link href={manage_organizations_path()} className="btn btn-outline-secondary">Tilbage</Link>
            <button type="submit" className="btn btn-primary" disabled={processing}>
              {processing ? "Gemmer..." : "Gem FAQ"}
            </button>
          </div>
        </form>
      </section>
    </>
  );
}

OrganizationFaqEdit.layout = (page) => (
  <AuthLayout
    header={{
      breadcrumbs: [
        { label: "Administration", href: manage_organizations_path() },
        { label: "Organisationer", href: manage_organizations_path() },
        { label: page.props.organization.name },
        { label: "FAQ" }
      ],
      title: "FAQ"
    }}
  >
    {page}
  </AuthLayout>
);

export default OrganizationFaqEdit;
